'use client';

import { useRef, useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface PinkTubeProps {
  tipRef: React.RefObject<THREE.Mesh | null>;
}

export default function PinkTube({ tipRef }: PinkTubeProps) {
  const geometryRef = useRef<THREE.BufferGeometry>(null);
  const meshRef = useRef<THREE.Mesh>(null);
  const scrollProgress = useRef(0);
  const lerpProgress = useRef(0);

  const tubularSegments = 450; // High segments for absolute smoothness
  const radialSegments = 64;   // Ultra-high radial resolution for a perfect cylinder

  // Programmatically generate the curve to match the reference image exactly.
  const { curve, curvePoints } = useMemo(() => {
    const pts: THREE.Vector3[] = [];
    
    // 1. Left hump extending way off-screen (bottom-left) to the valley
    pts.push(new THREE.Vector3(-14.0, -7.0, 1.0));
    pts.push(new THREE.Vector3(-9.5, -3.0, -1.0));
    pts.push(new THREE.Vector3(-6.0, 1.8, -2.5));  // hump peak (lowered to prevent top-clipping)
    pts.push(new THREE.Vector3(-3.0, -0.6, -5.0));
    pts.push(new THREE.Vector3(-0.5, -3.0, -6.5));  // valley
    pts.push(new THREE.Vector3(0.5, -0.5, -8.5));   // transition to loop start
    
    // 2. Mathematically generate the corkscrew (tirabuzón) loop
    // Clockwise 1.25 turns, starting at the left side of the loop and winding back
    const cx = 4.6;
    const cy = 1.6;
    const cz = -11.0;
    const R = 2.6;
    const pitch = 4.0;
    const rotY = 0.7;   // Tilts the loop so the left side is in the foreground
    const rotX = 0.15;  // Slight pitch forward
    const steps = 24;
    
    // Clockwise (decreasing theta) from Left (Math.PI) to Top (-1.5 * Math.PI)
    const startTheta = Math.PI;
    const endTheta = -1.5 * Math.PI;
    
    for (let i = 0; i <= steps; i++) {
      const frac = i / steps;
      const theta = startTheta + frac * (endTheta - startTheta);
      
      // Local helix coordinates (centered along local Z)
      const lx = R * Math.cos(theta);
      const ly = R * Math.sin(theta);
      const lz = pitch * (frac - 0.5);
      
      // Rotate around X-axis
      const cosX = Math.cos(rotX);
      const sinX = Math.sin(rotX);
      const ry = ly * cosX - lz * sinX;
      const rz1 = ly * sinX + lz * cosX;
      
      // Rotate around Y-axis
      const cosY = Math.cos(rotY);
      const sinY = Math.sin(rotY);
      const rx = lx * cosY + rz1 * sinY;
      const rz = -lx * sinY + rz1 * cosY;
      
      pts.push(new THREE.Vector3(cx + rx, cy + ry, cz + rz));
    }
    
    // 3. Tail section exiting to the top-right in the background
    pts.push(new THREE.Vector3(8.0, 6.5, -14.0));   // transition to tail
    pts.push(new THREE.Vector3(10.0, 9.5, -18.5));  // tail off-screen in background

    const c = new THREE.CatmullRomCurve3(pts);
    return {
      curve: c,
      curvePoints: c.getPoints(tubularSegments)
    };
  }, []);

  // GPU Tapering Shader: Thick on the left (uv.x = 0), thin on the right (uv.x = 1)
  const handleBeforeCompile = useMemo(() => {
    return (shader: any) => {
      shader.vertexShader = shader.vertexShader.replace(
        '#include <begin_vertex>',
        `
        #include <begin_vertex>
        // uv.x goes from 0.0 (hump/left) to 1.0 (tail/right)
        float maxRadius = 1.85;
        float minRadius = 0.65;
        float currentRadius = maxRadius - (maxRadius - minRadius) * pow(uv.x, 1.2);
        // Displace the vertex position along the vertex normal
        transformed += normal * (currentRadius - 1.0);
        `
      );
    };
  }, []);

  useEffect(() => {
    // Listen to native scroll to avoid GSAP / trigger loading order issues
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        scrollProgress.current = scrollTop / docHeight;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update dynamic position and rotation based on scroll progress
  useFrame((state) => {
    const elapsed = state.clock.getElapsedTime();
    
    // Smooth lerp scroll progress for lag-free inertial transition
    lerpProgress.current += (scrollProgress.current - lerpProgress.current) * 0.08;

    if (meshRef.current) {
      // Rotation
      meshRef.current.rotation.y = lerpProgress.current * Math.PI * 0.28 + Math.sin(elapsed * 0.1) * 0.02;
      meshRef.current.rotation.x = lerpProgress.current * Math.PI * 0.06;
      
      // Dramatic Zoom closer and pan left on scroll for premium spatial interaction
      meshRef.current.position.z = lerpProgress.current * 6.5;
      meshRef.current.position.x = -lerpProgress.current * 3.0;
    }

    // Point tipRef to a dummy group or position to keep other component refs satisfied
    if (tipRef && tipRef.current && curvePoints.length > 0) {
      const pos = curvePoints[tubularSegments];
      tipRef.current.position.copy(pos);
    }
  });

  return (
    <mesh ref={meshRef}>
      {curve && (
        <tubeGeometry
          ref={geometryRef}
          args={[curve, tubularSegments, 1.0, radialSegments, false]}
        />
      )}
      <meshPhysicalMaterial
        onBeforeCompile={handleBeforeCompile}
        color="#D01760"
        roughness={0.55}       // High roughness for a satin matte finish (no wet highlights)
        metalness={0.0}        // Matte silicone/plastic feel
        clearcoat={0.0}        // Zero clearcoat gloss
        reflectivity={0.4}
        sheen={1.5}            // Beautiful soft sheen velvet look
        sheenColor="#FFA3C4"
        sheenRoughness={0.5}
      />
    </mesh>
  );
}
